import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    TextField,
    Button,
    Paper,
    Alert,
    IconButton,
    Tooltip,
    LinearProgress,
    useMediaQuery,
    useTheme,
    CircularProgress
} from '@mui/material';
import { motion } from 'framer-motion';
import { 
    Lock, 
    Eye, 
    EyeOff, 
    Check, 
    X,
    Info,
    AlertCircle
} from 'lucide-react';
import Sidebar from '../Sidebar.js';
import './ChangePassword.css';
import { useAuth0 } from '@auth0/auth0-react';

// Import auth service
import { authService } from '../../../services/authService';

const PasswordRequirement = ({ met, text }) => (
    <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        color: met ? 'success.main' : 'text.secondary', 
        mb: 1 
    }}>
        {met ? <Check size={16} color="#4CAF50" /> : <X size={16} color="#757575" />}
        <Typography variant="body2" sx={{ ml: 1 }}>
            {text}
        </Typography>
    </Box>
);

const ChangePassword = () => {
    const theme = useTheme();
    const { getAccessTokenSilently } = useAuth0();
    
    // Device detection
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isSmallTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const isIPadAir = useMediaQuery('(min-width:768px) and (max-width:834px) and (height:1180px)');
    const isIPadMini = useMediaQuery('(min-width:744px) and (max-width:768px)');
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const isLargePhone = useMediaQuery('(min-width:390px) and (max-width:480px)');
    
    // Determine layout modes
    const useTabletLayout = isTablet || isIPadAir || isIPadMini;
    const useCompactLayout = isMobile || isLargePhone;
    
    // UI state
    const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
    const [darkMode, setDarkMode] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState(0);
    
    // Form state
    const [formData, setFormData] = useState({
        newPassword: '',
        confirmPassword: ''
    });
    
    // Requirements state
    const [requirements, setRequirements] = useState({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false,
        match: false
    });
    
    // API state
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    
    // Update sidebar state when screen size changes
    useEffect(() => {
        setSidebarOpen(!isMobile);
    }, [isMobile]);

    const checkPasswordRequirements = (password) => {
        const reqs = {
            length: password.length >= 8,
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            number: /[0-9]/.test(password),
            special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
        };

        const strength = Object.values(reqs).filter(Boolean).length * 20;
        setPasswordStrength(strength);
        
        setRequirements({
            ...reqs,
            match: password === formData.confirmPassword
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (name === 'newPassword') {
            checkPasswordRequirements(value);
        }
        if (name === 'confirmPassword') {
            setRequirements(prev => ({
                ...prev,
                match: value === formData.newPassword
            }));
        }
    };

    // Helper function to get specific error messages for unmet requirements
    const getRequirementErrors = () => {
        const errors = [];
        
        if (!requirements.length) errors.push('Password must be at least 8 characters long');
        if (!requirements.uppercase) errors.push('Password must contain at least one uppercase letter');
        if (!requirements.lowercase) errors.push('Password must contain at least one lowercase letter');
        if (!requirements.number) errors.push('Password must contain at least one number');
        if (!requirements.special) errors.push('Password must contain at least one special character');
        if (!requirements.match) errors.push('Passwords do not match');
        
        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Reset previous error messages
        setError(null);
        setSuccess(false);
        
        // Check if passwords are provided
        if (!formData.newPassword.trim()) {
            setError('Please enter a new password');
            return;
        }
        
        if (!formData.confirmPassword.trim()) {
            setError('Please confirm your new password');
            return;
        }
        
        // Validate requirements and show specific error messages
        if (!Object.values(requirements).every(Boolean)) {
            const requirementErrors = getRequirementErrors();
            
            if (requirementErrors.length === 1) {
                setError(requirementErrors[0]);
            } else if (requirementErrors.length <= 3) {
                setError(requirementErrors.join('. ') + '.');
            } else {
                setError('Please meet all password requirements listed below before submitting.');
            }
            return;
        }
        
        try {
            setLoading(true);
            
            // Get Auth0 token
            const token = await getAccessTokenSilently({
                audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                scope: 'openid profile email'
            });
            
            // Call the auth service to update password
            await authService.updatePassword({
                new_password: formData.newPassword
            }, token);
            
            // Show success and reset form
            setSuccess(true);
            setFormData({
                newPassword: '',
                confirmPassword: ''
            });
            setPasswordStrength(0);
            setRequirements({
                length: false,
                uppercase: false,
                lowercase: false,
                number: false,
                special: false,
                match: false
            });
            
        } catch (error) {
            console.error('Error updating password:', error);
            setError(error.friendlyMessage || 'Failed to update password. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const getStrengthColor = () => {
        if (passwordStrength <= 20) return '#f44336';
        if (passwordStrength <= 40) return '#ff9800';
        if (passwordStrength <= 60) return '#fdd835';
        if (passwordStrength <= 80) return '#8bc34a';
        return '#4caf50';
    };
    
    // Calculate content margin based on device and sidebar state
    const getContentMargin = () => {
        if (isMobile) return 0;
        if (isIPadAir || isIPadMini) {
            return sidebarOpen ? '240px' : '80px';
        }
        if (sidebarOpen) return '240px';
        return '80px';
    };

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'grey.50' }}>
            {/* Sidebar component */}
            <Sidebar 
                onToggle={() => setSidebarOpen(!sidebarOpen)}
                darkMode={darkMode}
            />

            <Box
                component={motion.div}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
                sx={{
                    flexGrow: 1,
                    ml: !isMobile ? getContentMargin() : 0,
                    transition: 'margin-left 0.3s',
                    p: useTabletLayout ? 2.5 : (isMobile ? 2 : 3),
                    width: '100%'
                }}
                className={isIPadAir || isIPadMini ? 'ipad-air-content' : ''}
            >
                <Container 
                    maxWidth="md" 
                    sx={{ 
                        px: useTabletLayout ? 2 : (isMobile ? 1 : 3),
                        pt: useTabletLayout ? 1 : 0
                    }}
                >
                    {/* Header Section */}
                    <Box sx={{ 
                        textAlign: 'center', 
                        mb: useTabletLayout ? 3 : (isMobile ? 3 : 4)
                    }}>
                        <Box sx={{ 
                            width: useTabletLayout ? 70 : (isMobile ? 64 : 80), 
                            height: useTabletLayout ? 70 : (isMobile ? 64 : 80), 
                            borderRadius: '50%', 
                            bgcolor: '#FF4775',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto',
                            mb: useTabletLayout ? 1.5 : (isMobile ? 1.5 : 2)
                        }}>
                            <Lock size={useTabletLayout ? 36 : (isMobile ? 32 : 40)} color="white" />
                        </Box>
                        <Typography 
                            variant={useTabletLayout ? "h5" : (isMobile ? "h5" : "h4")} 
                            gutterBottom
                        >
                            Change Password
                        </Typography>
                        <Typography 
                            color="textSecondary" 
                            sx={{ 
                                mb: useTabletLayout ? 2.5 : (isMobile ? 2 : 3),
                                px: isMobile ? 1 : 0,
                                fontSize: isMobile ? '0.875rem' : '1rem'
                            }}
                        >
                            Keep your account secure by creating a strong password
                        </Typography>
                    </Box>

                    {/* Main Content */}
                    <Paper 
                        component="form" 
                        onSubmit={handleSubmit}
                        sx={{ 
                            p: useCompactLayout ? 3 : 4, 
                            maxWidth: useTabletLayout ? 500 : (isMobile ? '100%' : 500), 
                            mx: 'auto',
                            borderRadius: useCompactLayout ? '12px' : '16px'
                        }}
                        elevation={1}
                        className={isIPadAir || isIPadMini ? 'ipad-paper' : ''}
                    >
                        {/* Status indicators */}
                        {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}
                        
                        {error && (
                            <Alert 
                                severity="error" 
                                icon={<AlertCircle size={useCompactLayout ? 18 : 20} />}
                                sx={{ mb: 2 }}
                            >
                                {error}
                            </Alert>
                        )}
                        
                        {success && (
                            <Alert 
                                severity="success" 
                                icon={<Check size={useCompactLayout ? 18 : 20} />}
                                sx={{ mb: 2 }}
                            >
                                Your password has been successfully updated.
                            </Alert>
                        )}

                        <Alert 
                            severity="info" 
                            icon={<Info size={useCompactLayout ? 18 : 20} />}
                            sx={{ 
                                mb: useCompactLayout ? 3 : 4,
                                backgroundColor: 'rgba(255, 71, 117, 0.1)',
                                color: '#666',
                                '& .MuiAlert-icon': {
                                    color: '#FF4775'
                                },
                                '& .MuiAlert-message': {
                                    fontSize: useCompactLayout ? '0.875rem' : '1rem'
                                }
                            }}
                        >
                            Create a strong password that you don't use for other accounts.
                        </Alert>

                        {/* New Password */}
                        <Box sx={{ mb: 3 }}>
                            <Typography 
                                variant={useCompactLayout ? "body2" : "subtitle2"} 
                                gutterBottom
                            >
                                New Password
                            </Typography>
                            <TextField
                                fullWidth
                                name="newPassword"
                                type={showNewPassword ? 'text' : 'password'}
                                value={formData.newPassword}
                                onChange={handleInputChange}
                                size={useCompactLayout ? "small" : "medium"}
                                disabled={loading}
                                InputProps={{
                                    endAdornment: (
                                        <IconButton
                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                            edge="end"
                                            size={useCompactLayout ? "small" : "medium"}
                                            disabled={loading}
                                        >
                                            {showNewPassword ? 
                                                <EyeOff size={useCompactLayout ? 18 : 20} /> : 
                                                <Eye size={useCompactLayout ? 18 : 20} />
                                            }
                                        </IconButton>
                                    ),
                                    style: { 
                                        fontSize: useCompactLayout ? '0.9rem' : '1rem',
                                        borderRadius: '8px'
                                    }
                                }}
                                className="password-field"
                            />
                            
                            {/* Password Strength Indicator */}
                            <Box sx={{ mt: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography 
                                        variant="caption" 
                                        color="textSecondary"
                                        sx={{ fontSize: useCompactLayout ? '0.7rem' : '0.75rem' }}
                                    >
                                        Password Strength
                                    </Typography>
                                    <Typography 
                                        variant="caption" 
                                        color={getStrengthColor()}
                                        sx={{ fontSize: useCompactLayout ? '0.7rem' : '0.75rem' }}
                                    >
                                        {passwordStrength <= 20 ? 'Very Weak' :
                                         passwordStrength <= 40 ? 'Weak' :
                                         passwordStrength <= 60 ? 'Fair' :
                                         passwordStrength <= 80 ? 'Good' :
                                         'Strong'}
                                    </Typography>
                                </Box>
                                <LinearProgress 
                                    variant="determinate" 
                                    value={passwordStrength}
                                    sx={{
                                        height: useCompactLayout ? 3 : 4,
                                        borderRadius: 2,
                                        backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                        '& .MuiLinearProgress-bar': {
                                            backgroundColor: getStrengthColor()
                                        }
                                    }}
                                />
                            </Box>
                        </Box>

                        {/* Confirm Password */}
                        <Box sx={{ mb: useCompactLayout ? 3 : 4 }}>
                            <Typography 
                                variant={useCompactLayout ? "body2" : "subtitle2"} 
                                gutterBottom
                            >
                                Confirm New Password
                            </Typography>
                            <TextField
                                fullWidth
                                name="confirmPassword"
                                type={showConfirmPassword ? 'text' : 'password'}
                                value={formData.confirmPassword}
                                onChange={handleInputChange}
                                size={useCompactLayout ? "small" : "medium"}
                                disabled={loading}
                                InputProps={{
                                    endAdornment: (
                                        <IconButton
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            edge="end"
                                            size={useCompactLayout ? "small" : "medium"}
                                            disabled={loading}
                                        >
                                            {showConfirmPassword ? 
                                                <EyeOff size={useCompactLayout ? 18 : 20} /> : 
                                                <Eye size={useCompactLayout ? 18 : 20} />
                                            }
                                        </IconButton>
                                    ),
                                    style: { 
                                        fontSize: useCompactLayout ? '0.9rem' : '1rem',
                                        borderRadius: '8px'
                                    }
                                }}
                                className="password-field"
                            />
                        </Box>

                        {/* Password Requirements */}
                        <Box 
                            sx={{ 
                                mb: useCompactLayout ? 3 : 4, 
                                p: useCompactLayout ? 1.5 : 2, 
                                bgcolor: 'rgba(0, 0, 0, 0.02)', 
                                borderRadius: 1 
                            }}
                            className="requirements-box"
                        >
                            <Typography 
                                variant={useCompactLayout ? "body2" : "subtitle2"} 
                                gutterBottom
                                sx={{ fontWeight: 500 }}
                            >
                                Password Requirements
                            </Typography>
                            <Box sx={{ 
                                display: 'grid',
                                gridTemplateColumns: useCompactLayout ? '1fr' : 'repeat(auto-fill, minmax(220px, 1fr))',
                                gap: 1
                            }}>
                                <PasswordRequirement met={requirements.length} text="At least 8 characters" />
                                <PasswordRequirement met={requirements.uppercase} text="At least one uppercase letter" />
                                <PasswordRequirement met={requirements.lowercase} text="At least one lowercase letter" />
                                <PasswordRequirement met={requirements.number} text="At least one number" />
                                <PasswordRequirement met={requirements.special} text="At least one special character" />
                                <PasswordRequirement met={requirements.match} text="Passwords match" />
                            </Box>
                        </Box>

                        {/* Submit Button - Only disabled when loading, not for requirements */}
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={loading}
                            sx={{
                                bgcolor: '#FF4775',
                                color: 'white',
                                py: useCompactLayout ? 1.25 : 1.5,
                                fontSize: useCompactLayout ? '0.9rem' : '1rem',
                                '&:hover': {
                                    bgcolor: '#FF2665'
                                },
                                '&.Mui-disabled': {
                                    bgcolor: 'rgba(255, 71, 117, 0.5)'
                                }
                            }}
                        >
                            {loading ? (
                                <>
                                    <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                                    Updating...
                                </>
                            ) : 'Update Password'}
                        </Button>
                    </Paper>
                </Container>
            </Box>
        </Box>
    );
};

export default ChangePassword;